import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@astro/auth'
import { ApiRequestError } from '@astro/api'
import { registerWithConfirmSchema } from '@astro/validators'
import { useToast } from '../../context/ToastContext'
import { InputField, PasswordField, MailIcon, UserIcon } from './AuthControls'
import CountryCodeSelect from './CountryCodeSelect'
import { toFieldErrors } from './formErrors'
import {
  DEFAULT_COUNTRY,
  DEFAULT_COUNTRY_ISO2,
  getCountryByIso2,
  isValidNationalNumber,
  toE164,
} from '../../data/countries'

/** Upstream validates first_name/last_name/mobile_no — surface those under our single fields. */
const remapUpstreamErrors = (fieldErrors) => {
  fieldErrors.name ??= fieldErrors.first_name || fieldErrors.last_name
  fieldErrors.mobile ??= fieldErrors.mobile_no
  return fieldErrors
}

/** Single-page account creation view. Role is hardcoded to "user" for now. */
export default function RegisterForm({ onSwitchToLogin }) {
  const { showToast } = useToast()
  const { register } = useAuth()
  const navigate = useNavigate()

  const [form, setForm] = useState({
    name: '',
    email: '',
    countryIso2: DEFAULT_COUNTRY_ISO2,
    mobile: '',
    password: '',
    password_confirmation: '',
    asAstrologer: false,
  })
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  const country = getCountryByIso2(form.countryIso2) ?? DEFAULT_COUNTRY
  const { min: minDigits, max: maxDigits } = country.phoneLength
  const phonePlaceholder =
    minDigits === maxDigits ? `${maxDigits}-digit mobile number` : `${minDigits}–${maxDigits} digit mobile number`

  /** Re-clamp the entered digits when the country's number length shrinks. */
  const handleCountryChange = (iso2) =>
    setForm((prev) => ({
      ...prev,
      countryIso2: iso2,
      mobile: prev.mobile.slice(0, (getCountryByIso2(iso2) ?? DEFAULT_COUNTRY).phoneLength.max),
    }))

  const handleSubmit = async (event) => {
    event.preventDefault()
    const parsed = registerWithConfirmSchema.safeParse(form)
    const fieldErrors = parsed.success ? {} : toFieldErrors(parsed.error.issues)

    // The shared schema's mobile rule is India-only (10 digits, starting 6-9),
    // so for every other country validate against that country's own length.
    if (country.iso2 !== DEFAULT_COUNTRY_ISO2) {
      if (isValidNationalNumber(country.iso2, form.mobile)) delete fieldErrors.mobile
      else fieldErrors.mobile = `Enter a valid ${country.name} mobile number`
    }

    if (Object.keys(fieldErrors).length) return setErrors(fieldErrors)
    setErrors({})

    // Upstream's `mobile_no` has always been a bare 10-digit Indian number —
    // keep that exact shape for India, and send E.164 for other countries so
    // the dial code travels with it.
    const data = {
      name: form.name.trim(),
      email: form.email,
      mobile: country.iso2 === DEFAULT_COUNTRY_ISO2 ? form.mobile : toE164(country.iso2, form.mobile),
      password: form.password,
      password_confirmation: form.password_confirmation,
    }

    // Astrologers register through their own onboarding endpoint (/v1/astrologers),
    // not the normal user-registration API: carry the entered details into the
    // guided wizard, which submits everything on its final step.
    if (form.asAstrologer) {
      const [first_name, ...rest] = data.name.split(/\s+/)
      navigate('/astrologer-register', {
        state: {
          prefill: {
            first_name,
            last_name: rest.join(' '),
            email: data.email,
            mobile_no: data.mobile,
            password: data.password,
          },
        },
      })
      return
    }

    setSubmitting(true)
    try {
      const result = await register({ ...data, role: 'user' })
      if (result.requiresLogin) {
        showToast(result.message || 'Account created — please sign in.')
        onSwitchToLogin(data.email)
      } else {
        showToast(`Welcome, ${result.user?.name || 'seeker'}!`)
        // Auto-login: AuthProvider is now "authenticated"; LoginPage's effect redirects.
      }
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setErrors(remapUpstreamErrors(toFieldErrors(err.error.details)))
        showToast(err.error.message || 'Registration failed. Please try again.')
      } else {
        showToast('Something went wrong. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div id="view-register-frame" className="auth-view-panel mt-36">
      <div className="space-y-2 mb-6 text-center sm:text-left">
        <h2 className="text-3xl font-royal tracking-wide text-astroDark uppercase font-medium">Create Account</h2>
        <p className="text-[11px] uppercase font-bold tracking-[0.25em] text-amber-800/60">Establish System Registry Profile</p>
        <p className="text-sm text-stone-500 pt-1">
          Existing Seeker?{' '}
          <button type="button" onClick={() => onSwitchToLogin()} className="text-goldAccent font-semibold hover:underline outline-none focus:underline">Sign In Instead</button>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <InputField
          label="Full Name"
          labelClass="block text-[11px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5"
          icon={<UserIcon />}
          type="text"
          autoComplete="name"
          required
          placeholder="Aarav Sharma"
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          error={errors.name}
        />
        <InputField
          label="Email Anchor Address"
          labelClass="block text-[11px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5"
          icon={<MailIcon />}
          type="email"
          autoComplete="email"
          required
          placeholder="seeker@path.com"
          value={form.email}
          onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
          error={errors.email}
        />

        <div>
          <label className="block text-[11px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5">Mobile Number</label>
          <div className="relative flex items-center">
            <CountryCodeSelect
              inputId="register-country-code"
              value={form.countryIso2}
              onChange={handleCountryChange}
            />
            <input
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              required
              placeholder={phonePlaceholder}
              className="vault-input-phone has-country"
              aria-invalid={Boolean(errors.mobile)}
              value={form.mobile}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, mobile: e.target.value.replace(/\D/g, '').slice(0, maxDigits) }))
              }
            />
          </div>
          {errors.mobile && <p className="text-xs text-red-600 mt-1.5 tracking-wide">{errors.mobile}</p>}
        </div>

        <PasswordField
          label="Security Access Password"
          labelClass="block text-[11px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5"
          autoComplete="new-password"
          required
          placeholder="Minimum 8 complex characters"
          value={form.password}
          onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
          error={errors.password}
        />
        <PasswordField
          label="Confirm Password"
          labelClass="block text-[11px] font-bold uppercase tracking-[0.15em] text-stone-600 mb-1.5"
          autoComplete="new-password"
          required
          placeholder="Re-enter your password"
          value={form.password_confirmation}
          onChange={(e) => setForm((prev) => ({ ...prev, password_confirmation: e.target.value }))}
          error={errors.password_confirmation}
        />

        {/* Astrologer registration checkbox card */}
        <div className="p-4 border border-amber-800/20 bg-amber-50/40 rounded-2xl flex items-start gap-3.5 my-5 transition-all hover:bg-amber-50/70">
          <input
            type="checkbox"
            id="astro-onboarding-check"
            className="mt-1 w-4 h-4 rounded border-amber-800/30 text-goldAccent focus:ring-goldAccent cursor-pointer"
            checked={form.asAstrologer}
            onChange={(e) => setForm((prev) => ({ ...prev, asAstrologer: e.target.checked }))}
          />
          <label htmlFor="astro-onboarding-check" className="flex flex-col select-none cursor-pointer">
            <span className="text-base font-semibold text-amber-900 leading-tight">I'm registering as an astrologer</span>
            <span className="text-xs text-stone-500 font-light leading-relaxed mt-0.5">Continue to a guided onboarding (profile, credentials, KYC) reviewed by our team before you go live.</span>
          </label>
        </div>

        <button type="submit" disabled={submitting} className="shine-btn w-full bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-sm tracking-[0.2em] uppercase py-4 rounded-xl shadow-lg shadow-goldPrimary/5 transition-transform hover:-translate-y-0.5 mt-2 disabled:opacity-60 disabled:cursor-not-allowed">
          {submitting ? 'Creating Profile…' : 'Commit New Profile →'}
        </button>
      </form>
    </div>
  )
}
